// src/layout/Sidebar.tsx
import { Drawer, Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { Link, matchPath, useLocation } from 'react-router-dom';
import logo from '../../assets/images/bundle-drop-logo.svg';
import {
  DashboardIcon,
  ProjectIcon,
  ActivityIcon,
  MembersIcon,
  UserPropertiesIcon,
  SettingsIcon,
  OrganizationsIcon,
  UsersAccessIcon,
  BillingIcon,
} from '@assets/icons';
import { useTheme } from '../../context/ThemeContext';
import { useGetMyOrganizationsQuery } from '@store/api/organizations/organizationsApi';
import { useTranslation } from 'react-i18next';
import styles from './Sidebar.module.css';

const { Sider } = Layout;
const ORGANIZATIONS_MENU_GROUP_KEY = '/organizations-group';
const ORGANIZATION_USERS_FALLBACK_KEY = '/organizations/__users';
const ORGANIZATION_BILLING_FALLBACK_KEY = '/organizations/__billing';
const ORGANIZATION_SETTINGS_FALLBACK_KEY = '/organizations/__settings';

type Props = {
  collapsed: boolean;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

const Sidebar = ({ collapsed, isMobile = false, mobileOpen = false, onMobileClose }: Props) => {
  const location = useLocation();
  const { data: organizations } = useGetMyOrganizationsQuery();
  const { colors } = useTheme();
  const { t } = useTranslation();

  const orgMatch = matchPath('/organizations/:orgSlug/*', location.pathname);
  const projectMatch = matchPath(
    '/organizations/:orgSlug/projects/:projectSlug/*',
    location.pathname
  );
  const projectMatchAlt = matchPath('/projects/:projectSlug/*', location.pathname);

  const isProjectContext = Boolean(projectMatch || projectMatchAlt);

  const activeOrgSlug =
    projectMatch?.params.orgSlug || orgMatch?.params.orgSlug || organizations?.[0]?.slug;

  const projectBasePath = projectMatch
    ? `/organizations/${projectMatch.params.orgSlug}/projects/${projectMatch.params.projectSlug}`
    : projectMatchAlt
      ? `/projects/${projectMatchAlt.params.projectSlug}`
      : '';

  const organizationMenuItems =
    organizations?.map(org => ({
      key: `/organizations/${org.slug}/projects`,
      icon: <ProjectIcon className={styles.navIcon} aria-hidden />,
      label: <Link to={`/organizations/${org.slug}/projects`}>{org.name}</Link>,
    })) || [];

  const orgUsersPath = activeOrgSlug
    ? `/organizations/${activeOrgSlug}/users`
    : ORGANIZATION_USERS_FALLBACK_KEY;
  const orgBillingPath = activeOrgSlug
    ? `/organizations/${activeOrgSlug}/billing`
    : ORGANIZATION_BILLING_FALLBACK_KEY;
  const orgSettingsPath = activeOrgSlug
    ? `/organizations/${activeOrgSlug}/settings`
    : ORGANIZATION_SETTINGS_FALLBACK_KEY;

  const projectDashboardPath = projectBasePath ? `${projectBasePath}/dashboard` : '/dashboard';
  const projectActivityPath = projectBasePath ? `${projectBasePath}/history` : '/projects';
  const projectMembersPath = projectBasePath ? `${projectBasePath}/members` : '/projects';
  const projectUserPropertiesPath = projectBasePath
    ? `${projectBasePath}/user-properties`
    : '/projects';
  const projectSettingsPath = projectBasePath ? `${projectBasePath}/settings` : '/projects';
  const projectRootPath = projectBasePath || '/projects';

  const orgMenuKeys = [
    '/organizations',
    orgUsersPath,
    orgBillingPath,
    orgSettingsPath,
    ...organizationMenuItems.map(item => item.key),
  ];
  const projectMenuKeys = [
    projectDashboardPath,
    projectRootPath,
    projectActivityPath,
    projectMembersPath,
    projectUserPropertiesPath,
    projectSettingsPath,
  ];

  const menuKeys = isProjectContext ? projectMenuKeys : orgMenuKeys;

  const activeKey =
    menuKeys
      .filter(key => location.pathname.startsWith(key))
      .sort((a, b) => b.length - a.length)[0] || location.pathname;

  const menuItems: MenuProps['items'] = isProjectContext
    ? [
        {
          key: projectDashboardPath,
          icon: <DashboardIcon className={styles.navIcon} aria-hidden />,
          label: projectBasePath ? (
            <Link to={projectDashboardPath}>{t('nav.dashboard')}</Link>
          ) : (
            t('nav.dashboard')
          ),
          disabled: !projectBasePath,
        },
        {
          key: projectRootPath,
          icon: <ProjectIcon className={styles.navIcon} aria-hidden />,
          label: projectBasePath ? (
            <Link to={projectRootPath}>{t('nav.project')}</Link>
          ) : (
            t('nav.project')
          ),
          disabled: !projectBasePath,
        },
        {
          key: projectActivityPath,
          icon: <ActivityIcon className={styles.navIcon} aria-hidden />,
          label: projectBasePath ? (
            <Link to={projectActivityPath}>{t('nav.activity')}</Link>
          ) : (
            t('nav.activity')
          ),
          disabled: !projectBasePath,
        },
        {
          key: projectMembersPath,
          icon: <MembersIcon className={styles.navIcon} aria-hidden />,
          label: projectBasePath ? (
            <Link to={projectMembersPath}>{t('nav.members')}</Link>
          ) : (
            t('nav.members')
          ),
          disabled: !projectBasePath,
        },
        {
          key: projectUserPropertiesPath,
          icon: <UserPropertiesIcon className={styles.navIcon} aria-hidden />,
          label: projectBasePath ? (
            <Link to={projectUserPropertiesPath}>{t('nav.userProperties')}</Link>
          ) : (
            t('nav.userProperties')
          ),
          disabled: !projectBasePath,
        },
        {
          key: projectSettingsPath,
          icon: <SettingsIcon className={styles.navIcon} aria-hidden />,
          label: projectBasePath ? (
            <Link to={projectSettingsPath}>{t('nav.settings')}</Link>
          ) : (
            t('nav.settings')
          ),
          disabled: !projectBasePath,
        },
      ]
    : [
        {
          key: ORGANIZATIONS_MENU_GROUP_KEY,
          icon: <OrganizationsIcon className={styles.navIcon} aria-hidden />,
          label: t('nav.organizations'),
          children: [
            {
              key: '/organizations',
              icon: <MembersIcon className={styles.navIcon} aria-hidden />,
              label: <Link to="/organizations">{t('nav.allOrganizations')}</Link>,
            },
            ...organizationMenuItems,
          ],
        },
        {
          key: orgUsersPath,
          icon: <UsersAccessIcon className={styles.navIcon} aria-hidden />,
          label: activeOrgSlug ? (
            <Link to={orgUsersPath}>{t('nav.usersAccess')}</Link>
          ) : (
            t('nav.usersAccess')
          ),
          disabled: !activeOrgSlug,
        },
        {
          key: orgBillingPath,
          icon: <BillingIcon className={styles.navIcon} aria-hidden />,
          label: activeOrgSlug ? (
            <Link to={orgBillingPath}>{t('nav.billing')}</Link>
          ) : (
            t('nav.billing')
          ),
          disabled: !activeOrgSlug,
        },
        {
          key: orgSettingsPath,
          icon: <SettingsIcon className={styles.navIcon} aria-hidden />,
          label: activeOrgSlug ? (
            <Link to={orgSettingsPath}>{t('nav.settings')}</Link>
          ) : (
            t('nav.settings')
          ),
          disabled: !activeOrgSlug,
        },
      ];

  const handleMainMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (isMobile && key !== ORGANIZATIONS_MENU_GROUP_KEY) {
      onMobileClose?.();
    }
  };

  const renderSidebarContent = (isCollapsed: boolean) => (
    <>
      <div className={styles.brand}>
        {isCollapsed ? (
          <img className={styles.brandLogoCollapsed} src={logo} alt={t('app.logoAlt')} />
        ) : (
          <>
            <img className={styles.brandLogo} src={logo} alt={t('app.logoAlt')} />
            <span className={styles.brandText}>{t('app.name')}</span>
          </>
        )}
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[activeKey]}
        defaultOpenKeys={isProjectContext ? [] : [ORGANIZATIONS_MENU_GROUP_KEY]}
        onClick={handleMainMenuClick}
        className={styles.menu}
        style={{ flex: 1, backgroundColor: colors.bg_500 }}
        items={menuItems}
      />
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={mobileOpen}
        onClose={onMobileClose}
        width={272}
        closable={false}
        className={styles.mobileDrawer}
        styles={{
          body: {
            padding: 0,
            background: colors.bg_500,
          },
          content: {
            background: colors.bg_500,
          },
        }}
      >
        <div className={styles.mobileSidebar}>{renderSidebarContent(false)}</div>
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={220}
      className={styles.sidebar}
      style={{
        background: colors.bg_500,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {renderSidebarContent(collapsed)}
    </Sider>
  );
};

export default Sidebar;
